// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import type {
  TimerStartPayload,
  TimerTickPayload,
  TimerCompletePayload,
  TimerPausedPayload,
  TimerResumedPayload,
  TimerCancelledPayload,
} from '../types/timer';

interface TimerStore {
  timerId: string | null;
  duration: number;
  remaining: number;
  phase: string;
  isRunning: boolean;
  isPaused: boolean;

  handleTimerStart: (payload: TimerStartPayload) => void;
  handleTimerTick: (payload: TimerTickPayload) => void;
  handleTimerComplete: (payload: TimerCompletePayload) => void;
  handleTimerPaused: (payload: TimerPausedPayload) => void;
  handleTimerResumed: (payload: TimerResumedPayload) => void;
  handleTimerCancelled: (payload: TimerCancelledPayload) => void;
  resetTimer: () => void;
}

const initialState = {
  timerId: null as string | null,
  duration: 0,
  remaining: 0,
  phase: '',
  isRunning: false,
  isPaused: false,
};

export const useTimerStore = create<TimerStore>()(set => ({
  ...initialState,

  handleTimerStart: payload =>
    set({
      timerId: payload.timer_id,
      duration: payload.duration,
      remaining: payload.duration,
      phase: payload.phase,
      isRunning: true,
      isPaused: false,
    }),

  handleTimerTick: payload => set({ remaining: payload.remaining }),

  handleTimerComplete: () => set({ remaining: 0, isRunning: false }),

  handleTimerPaused: payload => set({ remaining: payload.remaining, isPaused: true }),

  handleTimerResumed: payload => set({ remaining: payload.remaining, isPaused: false }),

  handleTimerCancelled: () => set(initialState),

  resetTimer: () => set(initialState),
}));
